/**
 * ==========================================================================
 * WISHLIST — Módulo central del sistema de favoritos (ro.ro crafts)
 *
 * Fuente de verdad: localStorage (clave "roro_wishlist").
 * Todo el toggle, contador y renderizado leen/escriben localStorage
 * para UI instantánea (0 latencia, sin login, sin red).
 *
 * Eventos custom:
 *   document → 'wishlist:updated' { detail: { items, action, productId } }
 *
 * Dependencias: ninguna (JS vanilla).
 * ==========================================================================
 */

(function () {
  'use strict';

  // ─── Clave y helpers de localStorage ───────────────────────────────────────
  const STORAGE_KEY = 'roro_wishlist';

  /**
   * Lee la wishlist de localStorage.
   * @returns {{ items: Array<{ id: number, handle: string, addedAt: string }> }}
   */
  function getWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.items)) return parsed;
      }
    } catch (_) {
      // JSON inválido — resetear
    }
    return { items: [] };
  }

  /**
   * Guarda la wishlist en localStorage y emite evento.
   * @param {{ items: Array }} wishlist
   * @param {string} action - 'add' | 'remove'
   * @param {number|null} productId
   */
  function saveWishlist(wishlist, action, productId) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: { items: wishlist.items, action: action, productId: productId },
      })
    );
  }

  /**
   * Comprueba si un producto está en la wishlist.
   * @param {number} id
   * @returns {boolean}
   */
  function isInWishlist(id) {
    return getWishlist().items.some(function (item) { return item.id === id; });
  }

  /**
   * Añade un producto a la wishlist (si no está ya).
   * @param {number} id
   * @param {string} handle
   */
  function addToWishlist(id, handle) {
    var wishlist = getWishlist();
    if (wishlist.items.some(function (item) { return item.id === id; })) return;
    wishlist.items.push({ id: id, handle: handle, addedAt: new Date().toISOString() });
    saveWishlist(wishlist, 'add', id);
  }

  /**
   * Quita un producto de la wishlist.
   * @param {number} id
   */
  function removeFromWishlist(id) {
    var wishlist = getWishlist();
    wishlist.items = wishlist.items.filter(function (item) { return item.id !== id; });
    saveWishlist(wishlist, 'remove', id);
  }

  /**
   * Toggle: añade o quita según estado actual.
   * @param {number} id
   * @param {string} handle
   * @returns {boolean} true si se añadió, false si se quitó
   */
  function toggleWishlist(id, handle) {
    if (isInWishlist(id)) {
      removeFromWishlist(id);
      return false;
    }
    addToWishlist(id, handle);
    return true;
  }

  /**
   * Devuelve la cantidad de items en la wishlist.
   * @returns {number}
   */
  function getCount() {
    return getWishlist().items.length;
  }

  // ─── API pública ───────────────────────────────────────────────────────────
  window.ROROWishlist = {
    get: getWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    has: isInWishlist,
    toggle: toggleWishlist,
    count: getCount,
  };

  // ─── Toast de notificación ─────────────────────────────────────────────────
  var toastEl = null;
  var toastTimer = null;

  /**
   * Crea el elemento toast (una sola vez, lazy).
   */
  function ensureToast() {
    if (toastEl) return;
    toastEl = document.createElement('div');
    toastEl.className = 'wishlist-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<svg class="wishlist-toast__icon" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.4" d="M9 16S1 11.5 1 5.8C1 2.7 3.3 1 5.7 1c1.5 0 2.9.8 3.3 2 .4-1.2 1.8-2 3.3-2C14.7 1 17 2.7 17 5.8 17 11.5 9 16 9 16Z"/>' +
      '</svg>' +
      '<span class="wishlist-toast__text"></span>';
    document.body.appendChild(toastEl);
  }

  /**
   * Muestra un toast con el mensaje indicado.
   * @param {string} message
   */
  function showToast(message) {
    ensureToast();
    toastEl.querySelector('.wishlist-toast__text').textContent = message;
    // Forzar reflow para reiniciar animación
    toastEl.classList.remove('is-visible');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2500);
  }

  // ─── Actualizar botones del DOM ────────────────────────────────────────────

  /**
   * Sincroniza TODOS los botones de wishlist del DOM con el estado real.
   */
  function syncAllButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-btn]');
    buttons.forEach(function (btn) {
      var id = parseInt(btn.getAttribute('data-product-id'), 10);
      if (!id) return;
      var active = isInWishlist(id);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute(
        'aria-label',
        active ? 'Видалити з обраного' : 'Додати до обраного'
      );
    });
  }

  /**
   * Sincroniza un solo botón por product ID.
   * @param {number} productId
   */
  function syncButtonsForProduct(productId) {
    var buttons = document.querySelectorAll(
      '[data-wishlist-btn][data-product-id="' + productId + '"]'
    );
    var active = isInWishlist(productId);
    buttons.forEach(function (btn) {
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute(
        'aria-label',
        active ? 'Видалити з обраного' : 'Додати до обраного'
      );
    });
  }

  // ─── Actualizar badge de la navbar ─────────────────────────────────────────

  function updateNavBadge() {
    // Por clase y no por id: el mismo marcado de acciones se pinta en la barra
    // y en la cabecera del drawer móvil, así que hay más de un contador vivo.
    var badges = document.querySelectorAll('[data-wishlist-count]');
    if (!badges.length) return;
    var count = getCount();
    Array.prototype.forEach.call(badges, function (badge) {
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    });
  }

  // ─── Click handler para los botones de wishlist ────────────────────────────

  /**
   * Delegación de eventos: escuchamos clicks en el body y filtramos
   * por [data-wishlist-btn]. Así funciona también para botones inyectados
   * dinámicamente (página wishlist, infinite scroll, etc.).
   */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    // Evitar que el click se propague a la tarjeta/enlace padre
    e.preventDefault();
    e.stopPropagation();

    var id = parseInt(btn.getAttribute('data-product-id'), 10);
    var handle = btn.getAttribute('data-product-handle') || '';
    if (!id) return;

    var added = toggleWishlist(id, handle);

    // Micro-animación
    btn.classList.remove('is-animating');
    void btn.offsetWidth; // forzar reflow
    btn.classList.add('is-animating');
    btn.addEventListener(
      'animationend',
      function () { btn.classList.remove('is-animating'); },
      { once: true }
    );

    // Toast en ucraniano
    showToast(added ? 'Додано до обраного' : 'Видалено з обраного');
  });

  // ─── Escuchar evento custom para sincronizar toda la UI ────────────────────

  document.addEventListener('wishlist:updated', function (e) {
    var detail = e.detail || {};
    if (detail.productId) {
      syncButtonsForProduct(detail.productId);
    } else {
      syncAllButtons();
    }
    updateNavBadge();
  });

  // ─── Sincronización entre pestañas vía evento "storage" ────────────────────

  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    // Otra pestaña cambió la wishlist — sincronizar UI
    syncAllButtons();
    updateNavBadge();
    // Re-renderizar página wishlist si estamos en ella
    if (document.getElementById('wishlist-grid')) {
      renderWishlistGrid();
    }
  });

  // ─── Página Wishlist: renderizado dinámico ──────────────────────

  // Sección de utilidad que devuelve UNA tarjeta ya renderizada por Liquid.
  // Ver `sections/roro-wishlist-card.liquid`.
  var CARD_SECTION = 'roro-wishlist-card';

  // El motor del carrusel (`roro-product-carousel.js`) se arranca una sola vez.
  var engineStarted = false;

  /**
   * Raíz del storefront con el locale ya aplicado ("/" o "/en/").
   * Sin esto, en un idioma secundario las URLs de producto redirigen.
   * @returns {string}
   */
  function shopRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /**
   * Pide a Shopify la tarjeta de un producto ya renderizada.
   *
   * La wishlist vive en localStorage, así que Liquid no puede saber qué pintar
   * al cargar la página. En vez de reconstruir la tarjeta a mano en JS (que es
   * lo que se hacía antes, y perdía el filtro `money`, los swatches, el badge y
   * el quick-add), se pide la misma tarjeta de la home con la Section Rendering
   * API sobre la URL del producto.
   *
   * @param {string} handle
   * @returns {Promise<{ card: Element, data: string|null }|null>}
   */
  function fetchCard(handle) {
    var url = shopRoot() + 'products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION;

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Card not found: ' + handle);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var card = doc.querySelector('[data-na-card]');
        if (!card) throw new Error('Empty card: ' + handle);
        var json = doc.querySelector('script[data-na-product]');
        return { card: card, data: json ? json.textContent.trim() : null };
      })
      .catch(function () {
        // Producto borrado o despublicado: se ignora y el resto se pinta igual.
        return null;
      });
  }

  /**
   * Estado vacío: sin tarjetas, con el CTA al catálogo.
   * @param {HTMLElement} gridEl
   * @param {HTMLElement|null} emptyEl
   */
  function showEmpty(gridEl, emptyEl) {
    gridEl.textContent = '';
    gridEl.hidden = true;
    if (emptyEl) emptyEl.hidden = false;
  }

  /**
   * Esqueleto de carga con tantas cajas como productos guardados: así la
   * página no salta de altura cuando llegan las tarjetas de verdad.
   * El script en línea de `sections/main-wishlist.liquid` pinta este mismo
   * marcado antes del primer pintado: si cambia uno, cambia el otro.
   * @param {HTMLElement} gridEl
   * @param {number} count
   */
  function showSkeleton(gridEl, count) {
    var html = '';
    for (var i = 0; i < count; i++) {
      html +=
        '<div class="wishlist-skeleton__item">' +
        '<div class="wishlist-skeleton__image"></div>' +
        '<div class="wishlist-skeleton__text"></div>' +
        '<div class="wishlist-skeleton__text wishlist-skeleton__text--short"></div>' +
        '</div>';
    }
    gridEl.innerHTML = html;
  }

  /**
   * Renderiza el grid de la página wishlist.
   */
  function renderWishlistGrid() {
    var gridEl = document.getElementById('wishlist-grid');
    if (!gridEl) return; // No estamos en la página wishlist

    var emptyEl = document.getElementById('wishlist-empty-state');
    var root = gridEl.closest('[data-na-root]');
    var wishlist = getWishlist();
    var items = (wishlist && Array.isArray(wishlist.items)) ? wishlist.items : [];

    if (items.length === 0) {
      showEmpty(gridEl, emptyEl);
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    gridEl.hidden = false;
    showSkeleton(gridEl, items.length);

    var atcLabel = (root && root.getAttribute('data-na-atc-label')) || '';
    var sheetId = root ? 'NaSheet-' + root.getAttribute('data-section-id') : '';

    Promise.all(items.map(function (item) { return fetchCard(item.handle); })).then(function (results) {
      var found = results.filter(Boolean);

      if (found.length === 0) {
        showEmpty(gridEl, emptyEl);
        return;
      }

      if (emptyEl) emptyEl.hidden = true;
      gridEl.hidden = false;

      var fragment = document.createDocumentFragment();
      var payload = [];

      found.forEach(function (result) {
        var card = result.card;
        var id = card.getAttribute('data-product-id');

        // El sheet al que apunta la tarjeta es el de ESTA página, no el de la
        // sección de utilidad que la ha renderizado.
        if (sheetId) {
          card.querySelectorAll('[data-na-quick-add]').forEach(function (btn) {
            btn.setAttribute('aria-controls', sheetId);
          });
        }

        // El texto del botón lo manda el ajuste de la sección de la página, que
        // es también el del botón del sheet: un solo sitio donde cambiarlo.
        if (atcLabel) {
          var atcText = card.querySelector('.na-card__atc-text');
          if (atcText) atcText.textContent = atcLabel;
          var atcBtn = card.querySelector('.na-card__atc');
          var title = card.querySelector('.na-card__title');
          if (atcBtn && title) {
            atcBtn.setAttribute('aria-label', atcLabel + ': ' + title.textContent.trim());
          }
        }

        // El envoltorio es lo que busca la animación de salida al quitar.
        var wrapper = document.createElement('div');
        wrapper.className = 'wishlist-page__item';
        wrapper.setAttribute('data-wishlist-card', id);
        wrapper.appendChild(card);
        fragment.appendChild(wrapper);

        if (result.data) payload.push(result.data);
      });

      gridEl.textContent = '';
      gridEl.appendChild(fragment);

      // El motor del carrusel lee este JSON al arrancar, así que hay que
      // rellenarlo antes de soltarle el freno.
      if (root && window.RoroProductCarousel) {
        var store = root.querySelector('[data-na-products]');
        if (store) store.textContent = '[' + payload.join(',') + ']';

        if (engineStarted) {
          // Repintado posterior (otra pestaña tocó la lista): re-inicializar
          // duplicaría los listeners y cada «añadir al carrito» metería dos
          // unidades. Solo hay que releer el JSON.
          window.RoroProductCarousel.refresh(root);
        } else {
          root.removeAttribute('data-na-init');
          window.RoroProductCarousel.init(root);
          engineStarted = true;
        }
      }

      // Los corazones llegan del servidor en estado inactivo.
      syncAllButtons();
    });
  }

  // ─── Eliminación en la página wishlist (animación de salida) ─────────

  document.addEventListener('wishlist:updated', function (e) {
    var detail = e.detail || {};
    if (detail.action !== 'remove' || !detail.productId) return;

    var card = document.querySelector('[data-wishlist-card="' + detail.productId + '"]');
    if (!card) return;

    card.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
    card.style.opacity = '0';
    card.style.transform = 'scale(0.95)';
    setTimeout(function () {
      card.remove();
      // Si no quedan tarjetas, mostrar estado vacío
      var gridEl = document.getElementById('wishlist-grid');
      var remainingItems = getWishlist().items;
      if (gridEl && (gridEl.children.length === 0 || remainingItems.length === 0)) {
        showEmpty(gridEl, document.getElementById('wishlist-empty-state'));
      }
    }, 250);
  });

  // ─── Inicialización al cargar el DOM ───────────────────────────────────────

  function init() {
    syncAllButtons();
    updateNavBadge();

    // Si estamos en la página wishlist, renderizar el grid
    if (document.getElementById('wishlist-grid')) {
      renderWishlistGrid();
    }
  }

  // Ejecutar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Sincronización con cuenta de cliente (TODO) ───────────────────────────
  //
  // La Storefront API con Storefront Access Token público NO permite escribir
  // metafields de customer desde el navegador (solo leerlos si tienen
  // "storefront access" habilitado).
  //
  // Para implementar sincronización con cuenta de cliente se necesita uno de:
  //
  // OPCIÓN A: Shopify App Proxy + Custom App
  //   1. Crear una Custom App en el Partner Dashboard con scope "write_customers"
  //   2. Crear un endpoint de App Proxy (ej: /apps/wishlist/sync)
  //   3. El proxy recibe el customer ID (autenticado por Shopify) y el array de IDs
  //   4. Escribe en el metafield del customer vía Admin API:
  //      - Namespace: "custom"
  //      - Key: "wishlist"
  //      - Tipo: "json" (array de product IDs)
  //      - Valor: { "items": [{ "id": 123, "handle": "...", "addedAt": "..." }] }
  //
  // OPCIÓN B: Cloudflare Worker / Vercel Edge Function (gratis)
  //   1. Desplegar un worker con el Admin API token
  //   2. Endpoint POST /wishlist/sync { customerId, items }
  //   3. El worker llama a la Admin API para escribir el metafield
  //
  // PASOS PARA HABILITAR:
  //   1. En Shopify Admin → Settings → Custom data → Customers → Add definition:
  //      - Name: "Wishlist"
  //      - Namespace and key: "custom.wishlist"
  //      - Type: JSON
  //      - Access: Storefront (lectura), Admin API (lectura + escritura)
  //   2. Implementar el endpoint elegido (A o B)
  //   3. Descomentar y completar las funciones de abajo:
  //
  // async function syncWishlistToCustomer() {
  //   var customerId = window.ROROcustomerId;
  //   if (!customerId) return;
  //   var wishlist = getWishlist();
  //   // TODO: POST al endpoint proxy/worker
  //   // await fetch('/apps/wishlist/sync', {
  //   //   method: 'POST',
  //   //   headers: { 'Content-Type': 'application/json' },
  //   //   body: JSON.stringify({ customerId: customerId, items: wishlist.items })
  //   // });
  // }
  //
  // async function loadWishlistFromCustomer() {
  //   var customerId = window.ROROcustomerId;
  //   if (!customerId) return;
  //   // TODO: GET del endpoint proxy/worker o leer el metafield vía Liquid
  //   // (se puede inyectar como variable JS global en theme.liquid si tiene storefront access)
  //   //
  //   // Merge: combinar localStorage + datos del servidor sin duplicados
  //   // var serverWishlist = await fetch('/apps/wishlist/load?customerId=' + customerId)
  //   //   .then(r => r.json());
  //   // var localWishlist = getWishlist();
  //   // var merged = mergeWishlists(localWishlist, serverWishlist);
  //   // saveWishlist(merged, 'merge', null);
  // }
  //
  // function mergeWishlists(local, server) {
  //   var merged = { items: [] };
  //   var seen = {};
  //   [].concat(local.items, server.items).forEach(function(item) {
  //     if (!seen[item.id]) {
  //       seen[item.id] = true;
  //       merged.items.push(item);
  //     }
  //   });
  //   return merged;
  // }
})();
